import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { ArrowLeft, Calendar, CheckCircle2, FileText, ShieldCheck, Target, UploadCloud, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatFramework, type ProgramPhase, useProgramDetails } from "@/hooks/use-compliance-programs";

const phaseMeta: Array<{ key: ProgramPhase; label: string; icon: typeof FileText }> = [
  { key: "policies", label: "Policies", icon: FileText },
  { key: "controls", label: "Controls", icon: ShieldCheck },
  { key: "evidence", label: "Evidence", icon: UploadCloud },
  { key: "team", label: "Team", icon: Users },
  { key: "audit_ready", label: "Audit Ready", icon: CheckCircle2 },
];

function statusTone(status: string) {
  if (["met", "complete", "approved", "accepted", "uploaded", "linked"].includes(status)) return "default";
  if (["partial", "in_progress", "under_review", "pending_approval"].includes(status)) return "secondary";
  return "outline";
}

export default function ProgramDetail() {
  const { id } = useParams();
  const { program, controls, policies, evidence, tasks, health, loading, error } = useProgramDetails(id);

  const controlsByDomain = useMemo(() => {
    return controls.reduce<Record<string, typeof controls>>((acc, control) => {
      const domain = control.framework_control?.domain ?? "General";
      acc[domain] = acc[domain] ?? [];
      acc[domain].push(control);
      return acc;
    }, {});
  }, [controls]);

  const target = program?.target_audit_date ? format(parseISO(program.target_audit_date), "MMM d, yyyy") : "No target date";

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-44" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (error || !program || !health) {
    return (
      <div className="space-y-4">
        <Button asChild variant="ghost">
          <Link to="/compliance/programs">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Programs
          </Link>
        </Button>
        <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">{error || "Program not found"}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-3">
          <Button asChild variant="ghost" className="-ml-3 w-fit">
            <Link to="/compliance/programs">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Programs
            </Link>
          </Button>
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <Badge variant="secondary">{formatFramework(program.framework)}</Badge>
              <Badge>{program.status.replace("_", " ")}</Badge>
              <Badge variant="outline">
                <Calendar className="mr-1 h-3 w-3" />
                {target}
              </Badge>
            </div>
            <h1 className="text-3xl font-medium tracking-tight">{program.name}</h1>
          </div>
        </div>
        <Button asChild variant="outline">
          <Link to="/compliance/tasks">Open generated tasks</Link>
        </Button>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="grid gap-6 lg:grid-cols-[1fr_360px] lg:items-center">
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Program readiness</span>
                <span className="font-semibold">{health.readinessPercent}%</span>
              </div>
              <Progress value={health.readinessPercent} className="h-3" />
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-md bg-muted/50 p-4">
                  <p className="text-sm text-muted-foreground">Controls met</p>
                  <p className="mt-1 text-2xl font-semibold">{health.metControls}/{health.totalControls}</p>
                </div>
                <div className="rounded-md bg-muted/50 p-4">
                  <p className="text-sm text-muted-foreground">Evidence ready</p>
                  <p className="mt-1 text-2xl font-semibold">{health.satisfiedEvidence}/{health.totalEvidence}</p>
                </div>
                <div className="rounded-md bg-muted/50 p-4">
                  <p className="text-sm text-muted-foreground">Open tasks</p>
                  <p className="mt-1 text-2xl font-semibold">{health.openTasks}</p>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {phaseMeta.map((phase) => {
                const Icon = phase.icon;
                const current = program.phase === phase.key;
                const phaseTasks = tasks.filter((task) => task.program_phase === phase.key);
                const complete = phaseTasks.length > 0 && phaseTasks.every((task) => task.status === "complete");
                return (
                  <div key={phase.key} className={`flex items-center gap-3 rounded-md border p-3 ${current ? "border-primary bg-primary/5" : "bg-background"}`}>
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{phase.label}</p>
                      <p className="text-xs text-muted-foreground">{phaseTasks.filter((task) => task.status !== "complete").length} open tasks</p>
                    </div>
                    {complete && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="controls" className="space-y-4">
        <TabsList>
          <TabsTrigger value="controls">Controls</TabsTrigger>
          <TabsTrigger value="policies">Policies</TabsTrigger>
          <TabsTrigger value="evidence">Evidence</TabsTrigger>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
        </TabsList>

        <TabsContent value="controls">
          <div className="space-y-4">
            {Object.entries(controlsByDomain).map(([domain, domainControls]) => (
              <Card key={domain}>
                <CardHeader>
                  <CardTitle className="text-base">{domain}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {domainControls.map((control) => (
                    <div key={control.id} className="rounded-md border p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <div className="mb-1 flex flex-wrap items-center gap-2">
                            <Badge variant="outline">{control.framework_control?.control_id}</Badge>
                            <Badge variant={statusTone(control.status)}>{control.status.replace("_", " ")}</Badge>
                          </div>
                          <h3 className="font-medium">{control.framework_control?.title}</h3>
                          <p className="mt-1 text-sm text-muted-foreground">{control.framework_control?.description}</p>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {(control.framework_control?.required_evidence_types?.length ?? 0)} evidence types
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="policies">
          <Card>
            <CardContent className="p-0">
              {policies.map((policy) => (
                <Link key={policy.id} to={`/policy/${policy.id}`} className="flex items-center justify-between gap-4 border-b p-4 last:border-b-0 hover:bg-muted/40">
                  <div className="min-w-0">
                    <p className="font-medium">{policy.title}</p>
                    <p className="text-sm text-muted-foreground">Version {policy.version} · customization {policy.customization_status.replace("_", " ")}</p>
                  </div>
                  <Badge variant={statusTone(policy.status)}>{policy.status.replace("_", " ")}</Badge>
                </Link>
              ))}
              {policies.length === 0 && <div className="p-8 text-center text-muted-foreground">No policies are linked to this program.</div>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="evidence">
          <Card>
            <CardContent className="p-0">
              {evidence.map((item) => (
                <div key={item.id} className="flex flex-col gap-3 border-b p-4 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-medium">{item.title}</p>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.recurrence && <Badge variant="outline">{item.recurrence}</Badge>}
                    <Badge variant={statusTone(item.status)}>{item.status}</Badge>
                  </div>
                </div>
              ))}
              {evidence.length === 0 && <div className="p-8 text-center text-muted-foreground">No evidence buckets were created.</div>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tasks">
          <Card>
            <CardContent className="p-0">
              {tasks.map((task) => (
                <Link key={task.id} to={`/compliance/tasks/${task.id}`} className="flex flex-col gap-2 border-b p-4 last:border-b-0 hover:bg-muted/40 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-medium">{task.title}</p>
                    <p className="text-sm text-muted-foreground">{task.program_phase?.replace("_", " ")} · {task.due_date ? format(parseISO(task.due_date), "MMM d, yyyy") : "No due date"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{task.priority.toUpperCase()}</Badge>
                    <Badge variant={statusTone(task.status)}>{task.status.replace("_", " ")}</Badge>
                  </div>
                </Link>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
